import { useMemo } from "react";
import { useSelector } from 'react-redux';
import { GreenroomState } from '../stores/types/types';
import useShotMemos from "./use-shot-memos";
import { FEED_TYPES } from '../stores/reducers/shots';
import { STATIC_ASSET_URL } from '../utils/content-templates';
import { FkTable } from "../stores/types/content";

const useMediaItemMemos = () => {
	const {
        currentShot,
    } = useShotMemos();

    const currentMediaItem = useSelector((state: GreenroomState) => state.GeneratedContentReducer.selectedMediaItem);
    const selectedMediaTemplate = useSelector((state: GreenroomState) => state.GeneratedContentReducer.selectedMediaTemplate);
    const media = useSelector((state: GreenroomState) => state.MediaReducer.media);
	const contentTemplates = useSelector((state: GreenroomState) => state.GeneratedContentReducer.contentTemplates);
	const currentGraphicId = useSelector((s: GreenroomState) => s.GraphicsReducer.currentGraphicId);

    const generatedContent = useSelector((s: GreenroomState) => s.GeneratedContentReducer.generatedContent?.filter(x => x.fkTable == FkTable.media) ?? []);

    const [mediaIdsFromFeeds, audioIdsFromFeeds] = useMemo(() => {
        return currentShot?.shot_data.shotFeeds.reduce((curr, acc) => {
            if (!acc.media) return curr;
            if (acc.feedType === FEED_TYPES.MEDIA) {
                curr[0].push(acc.media);
            } else if (acc.feedType === FEED_TYPES.MEDIA_AUDIO) {
                curr[1].push(acc.media);
            }
            return curr;
        }, [[], []] as [number[], number[]]);
    }, [currentShot?.shot_data.shotFeeds]);

    const currentGeneratedContent = useMemo(() => {
		if (currentMediaItem) {
			return generatedContent.find(x => x.fkId == currentMediaItem.id && x.fkTable == 'gr_media');
		} 
		return null;
	}, [currentMediaItem, generatedContent]);
    
	const currentTemplate = useMemo(() => {
        if (selectedMediaTemplate) {
            return contentTemplates.find((t) => t.type === selectedMediaTemplate);
        } else if (currentMediaItem) {
            const content = generatedContent.find((x) => x.fkId == currentMediaItem.id && x.fkTable == 'gr_media');
            if (content)
                return contentTemplates.find((t) => t.id == content.templateId);
            else
                return contentTemplates.find((t) => t.videoUrl?.replace('ASSET_URL', STATIC_ASSET_URL ?? '') === currentMediaItem?.images);
        } else {
            for (let i = 0; i < mediaIdsFromFeeds.length; i++) {
                const mediaItem = media.find((x) => x.id == mediaIdsFromFeeds[i]);
                if (mediaItem) {
                    const content = generatedContent.find((x) => x.fkId == mediaItem.id && x.fkTable == 'gr_media');
                    if (content)
                        return contentTemplates.find((t) => t.id == content.templateId);
                    else
                        return contentTemplates.find((t) => t.videoUrl?.replace('ASSET_URL', STATIC_ASSET_URL ?? '') === mediaItem?.images);
                }
            }
        }

		return null;
	}, [currentMediaItem, mediaIdsFromFeeds, selectedMediaTemplate]);

    const currentTemplateMediaItem = useMemo(() => {
        if (currentMediaItem?.template_type) {
            return currentMediaItem;
        } else if (currentGeneratedContent?.fkTable == 'gr_media' && currentGeneratedContent.templateId == currentTemplate?.id) {
            return media.find((x) => x.id === currentGeneratedContent.fkId);
        } else {
            for (const mediaId of [...mediaIdsFromFeeds, ...audioIdsFromFeeds]) {
                const mediaItem = media.find((m) => m.id === mediaId);
                if (mediaItem?.template_type) {
                    return mediaItem;
                }
            }

            return media.find((x) => currentTemplate?.videoUrl?.replace('ASSET_URL', STATIC_ASSET_URL ?? '') === x.images);
        }
    }, [currentTemplate, mediaIdsFromFeeds, audioIdsFromFeeds, currentMediaItem, media, currentGeneratedContent]);

    const currentAudioMediaItem = useMemo(() => {
        //  this audio settings menu modifies the first audio only feed that is a media file

        for (let i = currentShot?.shot_data.shotFeeds.length - 1; i > -1; i--) {
            const feed = currentShot?.shot_data.shotFeeds[i];

            if (feed.audioOnly || feed.feedType === FEED_TYPES.MEDIA_AUDIO) {
                const audioMediaItem = media.find((x) => x.id === feed.media);
                if (audioMediaItem) {
                    return audioMediaItem;
                }
            }
        }

        return null;
    }, [media, currentShot?.shot_data.shotFeeds]);
    
    return {
        currentGeneratedContent,
        currentTemplate,
        currentTemplateMediaItem,
        currentAudioMediaItem,
    };
}
export default useMediaItemMemos;

